// src/components/organizer/auth/OrganizerLogin.jsx
import React from "react";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { loginOrganizer, fetchOrganizerProfile, clearOrganizerError } from "@/store/slices/organizerSlice";
import { Mail, Lock, Eye, EyeOff, Building2 } from "lucide-react";
import { toast } from "react-toastify";
import ParticleBackground from "@/components/home/ParticleBackground";
import Button from "@/components/common/Button";
import TextInput from "@/components/common/TextInput";

const OrganizerLogin = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error } = useSelector((state) => state.organizer);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  React.useEffect(() => {
    dispatch(clearOrganizerError());
  }, [dispatch]);

  const onSubmit = async (data) => {
    try {
      await dispatch(loginOrganizer(data)).unwrap();
      await dispatch(fetchOrganizerProfile());
      toast.success("Login successful!");
      navigate("/organizer/dashboard");
    } catch (error) {
      toast.error(error.message || "Login failed");
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <ParticleBackground />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-8 transition-colors duration-300"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors duration-300">
            Organizer Portal
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2 transition-colors duration-300">
            Sign in to manage your events
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <TextInput
            label="Email Address"
            type="email"
            icon={<Mail className="w-5 h-5" />}
            placeholder="Enter your email"
            error={errors.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: "Invalid email address",
              },
            })}
          />

          <div className="relative">
            <TextInput
              label="Password"
              type={showPassword ? "text" : "password"}
              icon={<Lock className="w-5 h-5" />}
              placeholder="Enter your password"
              error={errors.password?.message}
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg"
            >
              {error}
            </motion.div>
          )}

          <Button
            type="submit"
            loading={isSubmitting}
            className="w-full py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
            size="lg"
          >
            Sign In
          </Button>
        </form>

        <div className="text-center mt-6 space-y-2">
          <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">
            Want to become an Organizer?{" "}
            <Link
              to="/organizer/apply"
              className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 font-semibold transition-colors duration-300"
            >
              Apply here
            </Link>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            <Link
              to="/login"
              className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              ← Back to User Login
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default OrganizerLogin;
